import { FC, useMemo } from "react";
import { useAlarmColor } from "../../../../../../../../hooks/use-alarm-color";
import { ITrainControlsResponse } from "../../interfaces";
import { Box, Typography } from "@mui/material";
import { useBlinkAnimation } from "../use-blink-animation";
import { resolveDelphiColor } from "../delphi-color";

interface ITiLabelProps {
  control: ITrainControlsResponse;
  flashUnacknowledgedAlarms: boolean;
  flashFaultAlarms: boolean;
}

export const TiLabel: FC<ITiLabelProps> = ({ control, flashUnacknowledgedAlarms, flashFaultAlarms }) => {
  const { value, props, alarmStatus, unacknowledgedEvent } = control;
  const alarmBgColor = useAlarmColor(Number(alarmStatus) || 0);

  const debugHasSettingsAX = control.debugHasSettingsAX;
  const objectClass = control.objectClass;

  const p: any = props || {};
  const bgColor = useMemo(() => {
    const explicitColor = resolveDelphiColor(p.BackGroundColor ?? p.BackgroundColor ?? p.BackColor);
    return explicitColor ?? alarmBgColor;
  }, [alarmBgColor, p.BackColor, p.BackGroundColor, p.BackgroundColor]);
  const textColor = useMemo(() => resolveDelphiColor(p.Font?.Color) ?? "#111111", [p.Font?.Color]);
  const fontFamily = useMemo(() => {
    const name = p.Font?.Name;
    if (typeof name === "string" && name.trim() !== "") {
      const q = name.includes(" ") ? `"${name.trim()}"` : name.trim();
      return `${q}, Tahoma, sans-serif`;
    }
    return "Tahoma, sans-serif";
  }, [p.Font?.Name]);
  const fontSize = useMemo(() => {
    const height = p.Font?.Height;
    return typeof height === "number" && Number.isFinite(height) ? Math.max(8, Math.abs(height)) : 13;
  }, [p.Font?.Height]);

  const captionFromProps =
    p.Caption != null && String(p.Caption).trim() !== ""
      ? String(p.Caption)
      : p.caption != null && String(p.caption).trim() !== ""
        ? String(p.caption)
        : null;

  const shouldShowPlaceholder =
    debugHasSettingsAX === false && (value === "0.0" || value === "0" || value === null || value === undefined);

  const displayText =
    captionFromProps != null
      ? captionFromProps
      : shouldShowPlaceholder && objectClass === "CAlarmStatusObject"
        ? "статус"
        : shouldShowPlaceholder
          ? "iLabel"
          : value;

  const textAlign =
    p.textAlign === "left" || p.textAlign === "right" || p.textAlign === "center" ? p.textAlign : "center";

  const shouldBlinkBg = flashUnacknowledgedAlarms && unacknowledgedEvent > 0;
  const shouldBlinkBorder = flashFaultAlarms && unacknowledgedEvent === 2;

  const { animation, keyframes } = useBlinkAnimation(shouldBlinkBg, shouldBlinkBorder, bgColor, "transparent");

  return (
    <Box
      sx={{
        position: "absolute",
        left: props.Left,
        top: props.Top,
        width: props.Width,
        height: props.Height,
        display: "flex",
        alignItems: "center",
        justifyContent: textAlign === "left" ? "flex-start" : textAlign === "right" ? "flex-end" : "center",
        boxSizing: "border-box",
        px: "4px",
        backgroundColor: bgColor,
        border: shouldBlinkBorder ? "2px solid transparent" : "none",

        animation,
        ...keyframes,
      }}
    >
      <Typography
        fontSize={`${fontSize}px`}
        fontFamily={fontFamily}
        fontWeight="bold"
        color={textColor}
        textAlign={textAlign}
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, maxWidth: "100%" }}
      >
        {displayText}
      </Typography>
    </Box>
  );
};
